import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RouterContext, goTo } from 't-lib-ui/router';
import { AppState } from '../context/general';
import { getPlanLimits } from '../schema-copy/userPlans';
import { useLimitUnread } from './LimitUnreadPopup';
import { usePauseFeed } from './PauseFeedPopup';
import FeedSettingsBadges from './FeedSettingsBadges';

interface FeedTileSettingsProps {
  feedId: string;
  /** The reader's view state, which the tracking and sort toggles change through the reader. */
  showRead?: 'u' | 'a';
  onSetShowRead?: (showRead: 'u' | 'a') => void;
  sortNewestFirst?: boolean;
  onSortChange?: (newestFirst: boolean) => void;
}

/** The followed feed's tracking/sort toggles and limits/filter/pause badges for the FeedTile's badge
 *  row. Unlike the header, it also shows the settings that are off, so they can be set from here.
 *  Re-renders on limit and pause changes (they notify as unread changes). */
const FeedTileSettings: React.FC<FeedTileSettingsProps> = ({ feedId, showRead, onSetShowRead, sortNewestFirst, onSortChange }) => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { openLimitUnread, limitUnreadPopup } = useLimitUnread();
  const { openPauseFeed, pauseFeedPopup } = usePauseFeed();
  const [, setVersion] = useState(0);
  useEffect(() => appState.feedEngine.onUnreadChange(() => setVersion(v => v + 1)), [appState.feedEngine]);

  // Like the header: keyword filters only exist on plans that apply them.
  const canFilterPosts = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').keywordFilters;
  return (
    <>
      <FeedSettingsBadges showUnset
        showRead={showRead} onToggleShowRead={onSetShowRead && (() => onSetShowRead(showRead == 'u' ? 'a' : 'u'))}
        sortNewestFirst={sortNewestFirst} onToggleSort={onSortChange && (() => onSortChange(!sortNewestFirst))}
        limits={appState.feedEngine.getFeedLimits(feedId)} onLimits={() => openLimitUnread(feedId)}
        filterActive={canFilterPosts && !!appState.feedEngine.getFeedFilter(feedId)}
        onFilter={canFilterPosts ? () => goTo(routerCtx, `/a/filter/${feedId}`) : undefined}
        pausedUntil={appState.feedEngine.getPausedUntil(feedId)} onPause={() => openPauseFeed(feedId)} />
      {/* Outside the badge row, so the dialogs don't inherit its smaller font size */}
      {createPortal(<>{limitUnreadPopup}{pauseFeedPopup}</>, document.body)}
    </>
  );
};

export default FeedTileSettings;
